import React from 'react';
import {App} from '../../app/AppSingleton';
import './BoardEditHeader.css';
import {AppSignals} from "../../app/AppSignals";
import {Icons} from "../../constants/icons";
import {Disposer} from "../../app/Signals";
import InlineEditWidget from '../elements/InlineEditWidget';
import {Board} from "../../models/types";

interface BoardEditHeaderProps {
	board: Board
}

interface BoardEditHeaderState {
	isSaving: boolean;
	saveStatus: 'idle' | 'success' | 'error';
	update: number;
	isEditingTitle: boolean;
}

interface HintSpec {
	id: string;
	icon: string;
	text: string;
	iconModifier?: string;
	customIcon?: React.ReactNode;
}

interface ButtonSpec {
	id: string,
	label: string,
	onClick: (()=>Promise<void>)| (() => void),
	disabled: ()=>boolean|undefined,
	classNameModifier: string,
	icon?: string
}

function BoardButton({spec, className}: { spec: ButtonSpec, className: string }) {
	return <button
		key={spec.id}
		onClick={spec.onClick}
		disabled={spec.disabled()}
		aria-disabled={spec.disabled()}
		className={className}
	>
		{spec.icon && <span>{spec.icon}</span>}
		<span>{spec.label}</span>
	</button>;
}

class BoardEditHeader extends React.Component<BoardEditHeaderProps, BoardEditHeaderState> {
	private app: App;
	private saveStatusTimeoutId: NodeJS.Timeout | null = null;
	private classBlock = 'board-edit-header';

	private hintSpecs: HintSpec[] = [
		{
			id: 'drag',
			icon: '☰',
			text: '— перетащите колонку для изменения порядка'
		},
		{
			id: 'delete',
			icon: '×',
			text: '— удалить колонку',
			iconModifier: 'delete'
		},
		{
			id: 'add',
			text: '— создать новую колонку',
			customIcon: (
				<div className="w-32 h-8 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-sm text-gray-500">
					+
				</div>
			)
		} as HintSpec
	];
	private disposer: Disposer = new Disposer();


	constructor(props: BoardEditHeaderProps) {
		super(props);
		this.app = App.getInstance();
		this.state = {
			isSaving: false,
			saveStatus: 'idle',
			update: 0,
			isEditingTitle: false
		};
	}

	componentDidMount(): void {
		this.disposer.register(AppSignals.boardUpdated, () => {
			this.forceUpdate();
		});
	}

	componentWillUnmount(): void {
		if (this.saveStatusTimeoutId) {
			clearTimeout(this.saveStatusTimeoutId);
		}
		this.disposer.dispose();
	}

	private get board() {
		return this.props.board;
	}

	private get editState() {
		return this.app.getEditState();
	}

	private get hasChanges() {
		return this.app.hasUnsavedChanges();
	}

	private get isSaveDisabled() {
		return this.state.isSaving || !this.hasChanges;
	}

	private getModifierClass = (baseClass: string, condition: boolean, modifier: string): string => {
		return condition ? `${baseClass}--${modifier}` : '';
	};

	private handleSave = async (): Promise<void> => {
		if (this.isSaveDisabled) {
			return;
		}

		this.setState({ isSaving: true, saveStatus: 'idle' });

		try {
			await this.app.editModeSave();
			this.setState({ saveStatus: 'success' });

			this.saveStatusTimeoutId = setTimeout(() => {
				this.setState({ saveStatus: 'idle' });
			}, 2000);
		} catch (error) {
			console.error('Save failed:', error);
			this.setState({ saveStatus: 'error' });
		} finally {
			this.setState({ isSaving: false });
		}
	};

	private handleCancel = (): void => {
		const { isSaving } = this.state;

		if (isSaving) {
			return;
		}

		if (this.hasChanges && !window.confirm('У вас есть несохраненные изменения. Отменить?')) {
			return;
		}

		this.app.editModeCancel();
	};

	private handleSaveBoardTitle = async (newTitle: string): Promise<void> => {
		try {
			this.app.boardSetTitle(newTitle);
			this.setState({ isEditingTitle: false });
		} catch (error) {
			console.error('Error saving board title:', error);
			// Оставляем в режиме редактирования при ошибке
		}
	};

	private formatTime = (date: Date | null): string => {
		if (!date) {
			return '';
		}
		return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	};

	private renderHeader(): React.ReactNode {
		const classContent = `${this.classBlock}__content`;

		return (
			<div className={classContent}>
				{this.renderLeftSection()}
				{this.renderRightSection()}
			</div>
		);
	}

	private renderLeftSection(): React.ReactNode {
		const classElement = `${this.classBlock}__left`;
		const classTitle = `${this.classBlock}__title`;
		const classTitleIcon = `${classTitle}-icon`;
		const classChangesIndicator = `${this.classBlock}__changes-indicator`;
		const classLastSaved = `${this.classBlock}__last-saved`;

		return (
			<div className={classElement}>
				<InlineEditWidget
					value={this.board.title}
					onSave={this.handleSaveBoardTitle}
					placeholder="Название доски"
					className={`${classTitle} ${classTitle}--editing`}
					maxLength={255}
					autoFocus={true}
					onEditStart={() => {
					}}
					onEditFinish={() => {
					}}
					view={({value, onEditStart}) => <h1
						className={classTitle}
						style={{cursor: 'pointer'}}
						title="Нажмите для редактирования"
					>
						<span className={classTitleIcon}>✎</span>
						{this.board.title || 'Доска задач'}
						{this.hasChanges && <span className={classChangesIndicator}>есть изменения</span>}
					</h1>}
				/>
				{this.renderSaveStatus()}
				{this.editState.lastSaved && (
					<span className={classLastSaved}>Последнее сохранение: {this.formatTime(this.editState.lastSaved)}</span>
				)}
			</div>
		);
	}

	private renderSaveStatus(): React.ReactNode {
		const { saveStatus } = this.state;
		const classElement = `${this.classBlock}__status`;
		const classDot = `${classElement}-dot`;

		const statusConfigs = {
			success: {
				text: 'Сохранено',
				dotModifier: 'success',
				elementModifier: 'success'
			},
			error: {
				text: 'Ошибка сохранения',
				dotModifier: 'error',
				elementModifier: 'error'
			}
		};

		if (saveStatus === 'idle') {
			return null;
		}

		const config = statusConfigs[saveStatus];
		const classElementModifier = `${classElement}--${config.elementModifier}`;
		const classDotModifier = `${classDot}--${config.dotModifier}`;

		return (
			<span className={`${classElement} ${classElementModifier}`.trim()}>
        		<span className={`${classDot} ${classDotModifier}`.trim()}/>
				{config.text}
      		</span>
		);
	}

	private renderRightSection(): React.ReactNode {
		const classElement = `${this.classBlock}__right`;
		const {isSaving} = this.state;

		return (
			<div className={classElement}>
				{isSaving && this.renderSavingIndicator()}
				{this.buttonSpecs.map((spec, idx) => {
					return <BoardButton key={idx}{...{
						spec: spec,
						className: `${this.classButton} ${spec.classNameModifier}`}}/>;
				})}
			</div>
		);
	}

	private renderSavingIndicator(): React.ReactNode {
		const classElement = `${this.classBlock}__saving-indicator`;
		const classSpinner = `${this.classBlock}__spinner`;

		return (
			<div className={classElement}>
				<div className={classSpinner}/>
				<span>Сохранение...</span>
			</div>
		);
	}

	private renderHints(): React.ReactNode {
		const classElement = `${this.classBlock}__hints`;
		const classContent = `${classElement}-content`;

		return (
			<div className={classElement}>
				<div className={classContent}>
					{this.hintSpecs.map(this.renderHint)}
				</div>
			</div>
		);
	}

	private renderHint = (hint: HintSpec): React.ReactNode => {
		const classElement = `${this.classBlock}__hint`;
		const classIcon = `${classElement}-icon`;
		const classText = `${classElement}-text`;

		const classIconModifier = hint.iconModifier
			? `${classIcon}--${hint.iconModifier}`
			: '';

		return (
			<div key={hint.id} className={classElement}>
				<div className={`${classIcon} ${classIconModifier}`.trim()}>
					{hint.customIcon || hint.icon}
				</div>
				<span className={classText}>{hint.text}</span>
			</div>
		);
	};

	render(): React.ReactNode {
		console.log("RENDERING BUTTON", {
			hasChanges: this.hasChanges,
			isSaving: this.state.isSaving,
			isSaveDisabled: this.isSaveDisabled,
			disabledAttr: this.isSaveDisabled
		});

		return (
			<header className={this.classBlock}>
				{this.renderHeader()}
				{this.renderHints()}
			</header>
		);
	}


	//<editor-fold desc="Specs">
	private classButton = `${this.classBlock}__button`;
	private buttonSpecs : ButtonSpec[] = [
		{
			id: 'cancel',
			label: 'Отменить',
			onClick: this.handleCancel,
			disabled: ()=>this.state.isSaving,
			classNameModifier: `${this.classButton}--cancel`
		},
		{
			id: 'save',
			label: 'Сохранить изменения',
			onClick: this.handleSave,
			disabled: ()=>this.isSaveDisabled ? true : undefined,
			classNameModifier: `${this.classButton}--save`,
			icon: Icons.Save
		}
	];
	//</editor-fold>

}

export default BoardEditHeader;